import React, { useEffect, useState } from 'react'
import { Box, Skeleton, Tab, TabList, TabPanel, Tabs, Typography } from '@mui/joy'
import Stepper, { Step } from '@components/Stepper'
import { useGetGuideByIdQuery, useGetGuidesListQuery } from '@endpoints/guidesEndpoint'
import { globalStyles } from '@styles/styles'
import { isForbidden } from '@utils'
import { useSession } from '@hooks/useSession'

const GuideContent = ({ id }: { id: number }) => {
  const { data: guide, isLoading } = useGetGuideByIdQuery({ id })

  if (isLoading)
    return (
      <Skeleton
        variant="rectangular"
        height={300}
        sx={{ borderRadius: globalStyles.borderRadius }}
      />
    )

  return (
    <Box
      className="ql-snow"
      sx={{ p: 1 }}
    >
      <div
        className="ql-editor"
        dangerouslySetInnerHTML={{ __html: guide?.content ?? '' }}
      />
    </Box>
  )
}

const GuidesViewer = () => {
  const { user } = useSession()
  const { data: guidesData, isLoading, isError, error, refetch } = useGetGuidesListQuery(undefined, { skip: !user })
  const guides = guidesData?.results ?? []
  const [activeId, setActiveId] = useState<number | null>(() => guidesData?.results?.[0]?.id ?? null)

  useEffect(() => {
    if (guides.length > 0 && activeId === null) {
      setActiveId(guides[0].id)
    }
  }, [guides])

  const steps: Step[] = [
    {
      title: 'Preguntas frecuentes',
      completed: true,
      content:
        guides.length === 0 ? (
          <Typography
            level="body-md-light"
            color="neutral"
          >
            No hay instructivos disponibles.
          </Typography>
        ) : (
          <Tabs
            value={activeId}
            onChange={(_, value) => setActiveId(value as number)}
            sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          >
            <TabList sx={{ flexShrink: 0, overflowX: 'auto' }}>
              {guides.map((guide) => (
                <Tab
                  color="primary"
                  key={guide.id}
                  value={guide.id}
                  sx={{
                    '&.Mui-selected .MuiTypography-root': { fontWeight: 'bold' },
                    '&:not(.Mui-selected) .MuiTypography-root': { color: globalStyles.secondaryTextColor },
                  }}
                >
                  <Typography level="title-lg-light">{guide.title}</Typography>
                </Tab>
              ))}
            </TabList>
            <TabPanel
              value={activeId ?? undefined}
              sx={{ overflowY: 'auto', flex: 1 }}
            >
              {activeId !== null && <GuideContent id={activeId} />}
            </TabPanel>
          </Tabs>
        ),
    },
  ]

  return (
    <Stepper
      style="joy"
      title="Preguntas frecuentes"
      isLoading={isLoading}
      error={isError && !isForbidden(error)}
      forbidden={isForbidden(error)}
      onErrorReload={refetch}
      hiddenButtons
      steps={steps}
    />
  )
}

export default GuidesViewer
